/* eslint-disable no-unused-vars */
import dayjs, { Dayjs } from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { CALENDAR_TYPE, LEARNING_MODE } from '../../constants'
import { useEffectAfterMounted } from '../../hooks/useEffectAfterMounted'
import { CalendarMode, ICourse, IEvent, IFilter, INorm } from '../../types'
import { splitEventsIntoDays } from '../../utils'
import CalendarLegend from './CalendarLegend/CalendarLegend'
import FilterCalendar from './FilterCalendar/FilterCalendar'
import HeaderCalendar from './HeaderCalendar/HeaderCalendar'
import LMSHeaderCalendar from './HeaderCalendar/LMSHeaderCalendar'
import LMSModeWeeklyCalendar from './LMSWeeklyCalendar'
import MonthlyCalendar from './MonthlyCalendar/MonthlyCalendar'
import styles from './SAPPCalendar.module.css'
import WeeklyCalendar from './WeeklyCalendar/WeeklyCalendar'
import clsx from 'clsx'
import LMSHeaderCalendarTablet from './HeaderCalendar/LMSHeaderCalendarTablet'
import { Grid } from 'antd'
import MonthlyCalendarMobile from './MonthlyCalendar/MonthlyCalendarMobile'
import LMSHeaderCalendarMobile from './HeaderCalendar/LMSHeaderCalendarMobile'
import { useDeviceType } from 'src/hooks/use-device-type'
import FilterCalendarMobile from './FilterCalendar/FilterCalendarMobile'
import ListEventInDayMobile from './ListEventInDayMobile'

dayjs.extend(updateLocale)
dayjs.updateLocale('en', {
  weekStart: 1
})

interface IAntdCalendarProps {
  events: IEvent[]
  norms?: INorm[]
  teacherName?: string
  showTeacherName?: boolean
  showWeeklyNorm?: boolean
  onOpenDetail?: (date: Date, events: IEvent[]) => void
  onOpenCreate?: (date: Date) => void
  onRefetchAPI: (startDate: Date, endDate: Date) => Promise<void>
  onEventDetail?: (event: IEvent) => void
  loading?: boolean
  type?: string
  courses?: ICourse[]
  onfilter?: (filter: IFilter) => void
  hasFilter?: boolean
  headerType?: string
}

const SAPPCalendar = ({
  teacherName,
  events,
  norms = [],
  showTeacherName = true,
  showWeeklyNorm = true,
  onOpenDetail,
  onOpenCreate,
  onRefetchAPI,
  onEventDetail,
  loading,
  type = 'OPS',
  courses,
  onfilter,
  hasFilter = false,
  headerType = CALENDAR_TYPE.OPS
}: IAntdCalendarProps) => {
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs())
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs())
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month')
  const [filter, setFilter] = useState<IFilter>({
    courseId: courses ? courses.map((item) => item.id) : [],
    isOpen: false,
    type: Object.values(LEARNING_MODE),
    day: ''
  })
  const deviceType = useDeviceType();

  const [eventDetail, setEventDetail] = useState<{event: IEvent | null, date: Dayjs} | null>(null)

  const filterRef = useRef<HTMLDivElement>(null)
  const startDate = useMemo(
    () => currentDate.startOf(calendarMode).startOf('week'),
    [currentDate, calendarMode]
  )

  const endDate = useMemo(
    () => currentDate.endOf(calendarMode).endOf('week'),
    [currentDate, calendarMode]
  )

  const splittedEvents = splitEventsIntoDays(events)
  splittedEvents.sort((a, b) => dayjs(a.startDate).diff(b.startDate))

  const handleFilterDate = (date: Dayjs) => {
    setCurrentDate(date)
  }

  useEffect(() => {
    onfilter && onfilter(filter)
  }, [filter])

  useEffectAfterMounted(() => {
    onRefetchAPI(startDate.toDate(), endDate.toDate())
  }, [startDate, endDate])

  useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      courseId: courses ? courses.map((item) => item.id) : []
    }))
  }, [courses])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setFilter((prev) => ({ ...prev, isOpen: false }))
      }
    }

    if (filter.isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [filter.isOpen])

  return (
    <div className={styles['sapp-calendar']}>
      <div className={clsx(styles['sapp-calendar__container'], 'max-w-[703px] md:max-w-full' )}>
        {headerType === CALENDAR_TYPE.LMS ? (
          <div className={"relative"}>
            {
            deviceType === 'desktop' &&
            <LMSHeaderCalendar
              title={teacherName}
              showTeacherName={showTeacherName}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              calendarMode={calendarMode}
              setCalendarMode={setCalendarMode}
              toggleTurnOnFilter={() =>{
                if(!filter.isOpen) {
                  setFilter((prev) => ({ ...prev, isOpen: !filter.isOpen }))
                }
              }
              }
              hasFilter={hasFilter}
              filter={filter}
            />
            }
            {
              deviceType === 'tablet' &&
              <LMSHeaderCalendarTablet
              title={teacherName}
              showTeacherName={showTeacherName}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              calendarMode={calendarMode}
              setCalendarMode={setCalendarMode}
              toggleTurnOnFilter={() =>{
                if(!filter.isOpen) {
                  setFilter((prev) => ({ ...prev, isOpen: !filter.isOpen }))
                }
              }
              }
              hasFilter={hasFilter}
              filter={filter}
              filterComponent = { filter.isOpen ?
                 <FilterCalendar
                  ref={filterRef}
                  filter={filter}
                  courses={courses}
                  setFilter={setFilter}
                  onRefetchAPI={(date) => handleFilterDate(dayjs(date))}
                /> : null
              }
            />
            }
            {
              deviceType === 'mobile' &&
              <LMSHeaderCalendarMobile
              title={teacherName}
              showTeacherName={showTeacherName}
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              calendarMode={calendarMode}
              setCalendarMode={setCalendarMode}
              toggleTurnOnFilter={() =>{
                if(!filter.isOpen) {
                  setFilter((prev) => ({ ...prev, isOpen: !filter.isOpen }))
                }
              }
              }
              hasFilter={hasFilter}
              filter={filter}
            />
            }
            {filter.isOpen && deviceType === 'desktop' &&(
                <FilterCalendar
                  ref={filterRef}
                  filter={filter}
                  courses={courses}
                  setFilter={setFilter}
                  onRefetchAPI={(date) => handleFilterDate(dayjs(date))}
                />
              )}
            <FilterCalendarMobile
              open={filter.isOpen && deviceType === 'mobile'}
              onClose={() => setFilter((prev) => ({ ...prev, isOpen: !filter.isOpen }))}
              filter={filter}
              courses={courses}
              setFilter={setFilter}
              onRefetchAPI={(date) => handleFilterDate(dayjs(date))}
            />
          </div>
        ) : (
          <HeaderCalendar
            title={teacherName}
            showTeacherName={showTeacherName}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            calendarMode={calendarMode}
            setCalendarMode={setCalendarMode}
          />
        )}


        {calendarMode === 'month' && (
          (deviceType === 'desktop' || deviceType === 'tablet') ? (
            <>
            <CalendarLegend />
            <MonthlyCalendar
              currentDate={currentDate}
              startDate={startDate}
              endDate={endDate}
              events={splittedEvents}
              norms={norms}
              showWeeklyNorm={showWeeklyNorm}
              onOpenDetail={onOpenDetail}
              onOpenCreate={onOpenCreate}
              onEventDetail={onEventDetail}
              loading={loading}
              eventDetail={eventDetail}
              setEventDetail={setEventDetail}
            />
            </>
          ) : (
            <div className='flex flex-col h-full gap-4'>
             <MonthlyCalendarMobile
              currentDate={currentDate}
              events={splittedEvents}
              onOpenDetail={onOpenDetail}
              onOpenCreate={onOpenCreate}
              onEventDetail={onEventDetail}
              loading={loading}
              onSelectDate={setSelectedDate}
              selectedDate={selectedDate}
            />
            <div className="flex-1">
              <ListEventInDayMobile
                events={splittedEvents}
                selectedDate={selectedDate.toDate()}
                onEventDetail={onEventDetail}
                eventDetail={eventDetail}
                setEventDetail={setEventDetail}
              />
            </div>
              <CalendarLegend />
            </div>
          )
        )}
        {calendarMode === 'week' && type === CALENDAR_TYPE.LMS && (
          <>
            {
              (deviceType === 'desktop' || deviceType === 'tablet') &&
              <CalendarLegend />
            }
            <LMSModeWeeklyCalendar
              startDate={startDate}
              endDate={endDate}
              events={splittedEvents}
              norms={norms}
              showWeeklyNorm={showWeeklyNorm}
              onOpenDetail={onOpenDetail}
              onOpenCreate={onOpenCreate}
              onEventDetail={onEventDetail}
              loading={loading}
              eventDetail={eventDetail}
              setEventDetail={setEventDetail}
            />
            {
              deviceType === 'mobile' &&
              <div className="mt-auto pt-2">
                <CalendarLegend />
              </div>
            }
          </>
        )}
        {calendarMode === 'week' && type === CALENDAR_TYPE.OPS && (
          <WeeklyCalendar
            startDate={startDate}
            endDate={endDate}
            events={splittedEvents}
            norms={norms}
            showWeeklyNorm={showWeeklyNorm}
            onOpenDetail={onOpenDetail}
            onOpenCreate={onOpenCreate}
            onEventDetail={onEventDetail}
            loading={loading}
          />
        )}
      </div>
    </div>
  )
}

export default SAPPCalendar
